// Doctor/admin-only settings page to edit the clinic's brand identity
// (name, address, phones, doctor credentials, registration, logo, footer).
// All fields flow through /api/settings/clinic and are picked up by every
// printed prescription, diet plan, education handout, WhatsApp share block
// and the sign-in page on next render.
import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast, Toaster } from "sonner";
import { Loader2, Save, Upload, RefreshCw, Building2, User2, Phone, FileText, Image as ImageIcon, Pill, ArrowUp, ArrowDown, ShieldCheck, Download, HardDriveUpload } from "lucide-react";
import api from "../lib/api";
import { CLINIC, setClinic, LogoTile, doctorPhotoUrl } from "../lib/clinic";
import { KNOWN_COMPANIES } from "../lib/brandCompanies";

const emptyProfile = {
  name: "", short: "", address: "", phones: [], email: "", website: "",
  doctorName: "", qualifications: "", regNo: "", footer: "", logo_url: "",
  specialties: "", timings: "", doctor_photo_url: "", services_whatsapp: "",
  print_header_style: "detailed",
  brand_color: "#DB2777",
  doctors: [],
  preferred_companies: KNOWN_COMPANIES.slice(),
};

const HEADER_STYLES = [
  { key: "detailed", label: "Detailed", hint: "Logo + clinic name + full address + all phones + doctor block (default)" },
  { key: "compact", label: "Compact", hint: "Logo + clinic name on one line + phones + doctor block on the right" },
  { key: "logo-only", label: "Logo only", hint: "Just the logo — for clinics using pre-printed letterhead" },
];

export default function ClinicProfile() {
  const [form, setForm] = useState(emptyProfile);
  const [phonesRaw, setPhonesRaw] = useState("");    // comma-separated for the input
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/clinic");
      const c = r.data.clinic || {};
      setForm({ ...emptyProfile, ...c });
      setPhonesRaw((c.phones || []).join(", "));
    } catch (e) {
      toast.error("Failed to load clinic profile");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Doctors — helpers to add/remove/edit a specific row inside the array.
  // Each doctor has a stable `id` so signature uploads stay attached even
  // when the operator reorders / renames doctors.
  const addDoctor = () => {
    const nextId = `d${Date.now().toString(36)}`;
    upd("doctors", [...(form.doctors || []), { id: nextId, name: "", qualifications: "", regNo: "", signature_url: "" }]);
  };
  const removeDoctor = (id) => upd("doctors", (form.doctors || []).filter((d) => d.id !== id));
  const updDoctor = (id, patch) => upd("doctors", (form.doctors || []).map((d) => d.id === id ? { ...d, ...patch } : d));

  // Preferred pharma companies — ordered list. Doctor can drag/reorder
  // (up/down) and disable individual entries. Removed entries are appended
  // to the tail (disabled) so re-enabling is one click away.
  const prefList = form.preferred_companies || [];
  const disabledCompanies = KNOWN_COMPANIES.filter((c) => !prefList.includes(c));
  const moveCompany = (co, dir) => {
    const list = prefList.slice();
    const i = list.indexOf(co);
    if (i < 0) return;
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    upd("preferred_companies", list);
  };
  const disableCompany = (co) => upd("preferred_companies", prefList.filter((c) => c !== co));
  const enableCompany = (co) => upd("preferred_companies", [...prefList, co]);
  const resetCompanies = () => upd("preferred_companies", KNOWN_COMPANIES.slice());

  const uploadSignature = async (doctorId, file) => {
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) { toast.error("Signature must be under 1 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/settings/clinic/doctors/${doctorId}/signature`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      updDoctor(doctorId, { signature_url: r.data.signature_url });
      // Refresh CLINIC so the doctor block on any open Rx re-renders with the new sig.
      const full = await api.get("/settings/clinic");
      setClinic(full.data.clinic);
      toast.success("Signature saved — prints will use it immediately");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signature upload failed");
    }
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Clinic name is required"); return; }
    if (!form.doctorName?.trim()) { toast.error("Doctor name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        phones: phonesRaw.split(",").map((p) => p.trim()).filter(Boolean),
      };
      const r = await api.put("/settings/clinic", payload);
      setClinic(r.data.clinic);   // update in-memory CLINIC so all prints refresh
      toast.success("Clinic profile saved — printed prescriptions will use these values immediately");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadDoctorPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Photo must be under 3 MB"); e.target.value = ""; return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/settings/clinic/doctor-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newUrl = r.data.doctor_photo_url;
      upd("doctor_photo_url", newUrl);
      setClinic({ doctor_photo_url: newUrl });
      toast.success("Photo uploaded — showing everywhere immediately");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); e.target.value = ""; return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/settings/clinic/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newUrl = r.data.logo_url;
      upd("logo_url", newUrl);
      setClinic({ logo_url: newUrl });
      toast.success("Logo uploaded — showing on every print immediately");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (loading) return (
    <Layout>
      <div className="min-h-[40vh] flex items-center justify-center text-slate-500">
        <Loader2 size={16} className="animate-spin mr-2" /> Loading clinic profile…
      </div>
    </Layout>
  );

  return (
    <Layout>
      <Toaster />
      <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="clinic-profile-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
              <Building2 size={22} className="text-pink-600" /> Clinic profile
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Update your clinic&rsquo;s name, contact details, doctor credentials and logo.
              Every printed prescription, diet plan, exercise plan, education handout,
              WhatsApp share block and the sign-in page will use these values.
            </p>
          </div>
          <Button onClick={load} variant="outline" size="sm" className="gap-1.5" data-testid="clinic-profile-reload">
            <RefreshCw size={12} /> Reload
          </Button>
        </div>

        {/* Quick-access chips to sibling admin pages */}
        <div className="flex flex-wrap gap-2">
          <a
            href="/settings/diet-defaults"
            className="text-[11px] px-2.5 py-1 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 inline-flex items-center gap-1"
            data-testid="link-diet-defaults"
          >
            🍽️ Diet — condition defaults tuner
          </a>
        </div>

        {/* Live preview strip — mirrors the top of every print. */}
        <div className="border border-slate-200 rounded-md p-4 bg-slate-50">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Live preview</div>
          <div className="flex items-start gap-3">
            <LogoTile className="h-16 w-16" padding="p-2" />
            <div className="flex-1">
              <div className="font-heading text-lg font-bold text-slate-900">{form.name || CLINIC.name}</div>
              <div className="text-xs text-slate-700 font-medium">{form.doctorName || CLINIC.doctorName}</div>
              <div className="text-[10px] text-slate-500 leading-snug">{form.qualifications || CLINIC.qualifications}</div>
              <div className="text-[10px] text-slate-500">{form.regNo || CLINIC.regNo}</div>
              <div className="text-[10px] text-slate-600 mt-1">{form.address || CLINIC.address}</div>
              <div className="text-[10px] text-slate-600 tabular">{phonesRaw || (CLINIC.phones || []).join(" · ")}</div>
              {form.footer && <div className="text-[9px] text-slate-500 italic mt-1">{form.footer}</div>}
            </div>
          </div>
        </div>

        {/* Logo upload */}
        <section className="space-y-2">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <ImageIcon size={14} className="text-pink-600" /> Logo
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <LogoTile className="h-16 w-16" padding="p-2" />
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 text-xs" data-testid="clinic-profile-logo-upload">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {form.logo_url ? "Replace logo" : "Upload logo"} (PNG / JPG / SVG, ≤ 2 MB)
              <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={uploadLogo} className="hidden" />
            </label>
            {form.logo_url && (
              <span className="text-[10px] text-slate-500 truncate max-w-md">{form.logo_url}</span>
            )}
          </div>
          <div className="text-[10px] text-slate-500">Square or slightly-tall logos work best. SVG is preferred for crisp printing.</div>
        </section>

        {/* Branding — doctor photo, specialties, timings */}
        <section className="space-y-3">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <User2 size={14} className="text-pink-600" /> Branding — photo, specialties &amp; timings
          </div>
          <div className="text-[11px] text-slate-600 leading-snug">
            Shown on the sign-in page, patient portal, verify-Rx page, printed diet / exercise / education
            handouts and appended as a signature to every WhatsApp message.
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <img
              key={form.doctor_photo_url || "default"}
              src={form.doctor_photo_url ? `${process.env.REACT_APP_BACKEND_URL || ""}${form.doctor_photo_url}` : doctorPhotoUrl()}
              alt="Doctor"
              className="h-16 w-16 rounded-full object-cover object-top ring-2 ring-pink-400"
              data-testid="clinic-profile-doctor-photo-preview"
            />
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 text-xs" data-testid="clinic-profile-doctor-photo-upload">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {form.doctor_photo_url ? "Replace photo" : "Upload photo"} (PNG / JPG / WebP, ≤ 3 MB)
              <input type="file" accept=".png,.jpg,.jpeg,.webp" onChange={uploadDoctorPhoto} className="hidden" />
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Fld label="Specialties (shown on handouts & WhatsApp signature)">
              <Input value={form.specialties} onChange={(e) => upd("specialties", e.target.value)} placeholder="Diabetes · Thyroid · Critical Care" data-testid="clinic-profile-specialties" />
            </Fld>
            <Fld label="Clinic timings">
              <Input value={form.timings} onChange={(e) => upd("timings", e.target.value)} placeholder="Mon–Sat: 10:00 AM – 8:00 PM" data-testid="clinic-profile-timings" />
            </Fld>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Fld label="Services WhatsApp number (with country code)">
              <Input
                value={form.services_whatsapp || ""}
                onChange={(e) => upd("services_whatsapp", e.target.value.replace(/\D/g, ""))}
                placeholder="e.g. 919876543210"
                className="tabular"
                data-testid="clinic-profile-services-whatsapp"
              />
              <div className="text-[10px] text-slate-500 mt-1">
                When set, the patient portal shows "Request a Service" buttons (diet plan, medicine delivery,
                lab sample collection) that open a WhatsApp chat to this number. Leave blank to hide.
              </div>
            </Fld>
          </div>
        </section>

        {/* Clinic details */}
        <section className="space-y-3">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Building2 size={14} className="text-pink-600" /> Clinic details
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Fld label="Clinic name" required>
              <Input value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Your clinic name" data-testid="clinic-profile-name" />
            </Fld>
            <Fld label="Short name">
              <Input value={form.short} onChange={(e) => upd("short", e.target.value)} placeholder="Short name for headers" data-testid="clinic-profile-short" />
            </Fld>
            <Fld label="Address" span2>
              <Input value={form.address} onChange={(e) => upd("address", e.target.value)} placeholder="Street, landmark, city, PIN" data-testid="clinic-profile-address" />
            </Fld>
            <Fld label="Phone numbers (comma-separated)" span2>
              <Input value={phonesRaw} onChange={(e) => setPhonesRaw(e.target.value)} placeholder="020 12345678, 9876543210" data-testid="clinic-profile-phones" />
            </Fld>
            <Fld label="Email">
              <Input value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="clinic@example.com" data-testid="clinic-profile-email" />
            </Fld>
            <Fld label="Website">
              <Input value={form.website} onChange={(e) => upd("website", e.target.value)} placeholder="https://example.com" data-testid="clinic-profile-website" />
            </Fld>
          </div>
        </section>

        {/* Doctor credentials */}
        <section className="space-y-3">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <User2 size={14} className="text-pink-600" /> Doctor credentials
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Fld label="Doctor name" required>
              <Input value={form.doctorName} onChange={(e) => upd("doctorName", e.target.value)} placeholder="Dr Full Name" data-testid="clinic-profile-doctor-name" />
            </Fld>
            <Fld label="Medical registration no.">
              <Input value={form.regNo} onChange={(e) => upd("regNo", e.target.value)} placeholder="R No. 2001/03/1657" data-testid="clinic-profile-regno" />
            </Fld>
            <Fld label="Qualifications" span2>
              <Input value={form.qualifications} onChange={(e) => upd("qualifications", e.target.value)} placeholder="MBBS, Fellowship in Diabetes, ..." data-testid="clinic-profile-quals" />
            </Fld>
          </div>
        </section>

        {/* Print header layout */}
        <section className="space-y-2">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <FileText size={14} className="text-pink-600" /> Print header layout
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {HEADER_STYLES.map((s) => {
              const active = form.print_header_style === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => upd("print_header_style", s.key)}
                  className={`text-left border rounded-md p-3 text-xs transition ${active ? "border-pink-500 bg-pink-50 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  data-testid={`clinic-profile-header-style-${s.key}`}
                >
                  <div className={`font-semibold ${active ? "text-pink-800" : "text-slate-800"}`}>{s.label}</div>
                  <div className="text-[10px] text-slate-600 mt-1 leading-snug">{s.hint}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Brand colour — accent on kit headers/footers + printed Rx */}
        <section className="space-y-2">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <FileText size={14} className="text-pink-600" /> Brand colour
          </div>
          <div className="text-[11px] text-slate-500">
            Used on education-kit headers &amp; footers, portal kit cards and the accent colour of the printed prescription.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(form.brand_color || "") ? form.brand_color : "#DB2777"}
              onChange={(e) => upd("brand_color", e.target.value)}
              className="h-9 w-14 rounded border border-slate-200 cursor-pointer bg-white p-1"
              data-testid="clinic-profile-brand-color"
            />
            <Input
              value={form.brand_color || ""}
              onChange={(e) => upd("brand_color", e.target.value)}
              placeholder="#DB2777"
              className="w-28 text-xs"
              data-testid="clinic-profile-brand-color-hex"
            />
            {["#DB2777", "#2563EB", "#0D9488", "#059669", "#7C3AED", "#DC2626", "#EA580C", "#334155"].map((c) => (
              <button
                key={c}
                type="button"
                title={c}
                onClick={() => upd("brand_color", c)}
                className={`h-7 w-7 rounded-full border-2 transition ${form.brand_color === c ? "border-slate-900 scale-110" : "border-white shadow"}`}
                style={{ backgroundColor: c }}
                data-testid={`clinic-profile-brand-swatch-${c.slice(1)}`}
              />
            ))}
          </div>
        </section>

        {/* Backup & data safety */}
        <BackupSection />

        {/* Google Drive prescription archive */}
        <GDriveSection />

        {/* WhatsApp Automation provision — dormant until a BSP is enabled */}
        <WaGatewaySection />

        {/* Doctors list — for multi-doctor clinics. First entry is the default
            signer; per-visit override via the Doctor picker on the visit page. */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              <User2 size={14} className="text-pink-600" /> Doctors ({(form.doctors || []).length})
            </div>
            <Button size="sm" variant="outline" onClick={addDoctor} className="gap-1.5 text-[11px]" data-testid="clinic-profile-add-doctor">
              <User2 size={11} /> Add doctor
            </Button>
          </div>
          {(form.doctors || []).length === 0 && (
            <div className="text-[11px] text-slate-500 border border-dashed border-slate-200 rounded-md p-3">
              No doctors added yet — the legacy single-doctor block above is used. Click <em>Add doctor</em> to enable per-Rx doctor selection and per-doctor signatures.
            </div>
          )}
          {(form.doctors || []).map((d, i) => (
            <div key={d.id} className="border border-slate-200 rounded-md p-3 bg-slate-50/50 space-y-2" data-testid={`clinic-profile-doctor-${i}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Doctor #{i + 1} · <span className="text-slate-400 normal-case">id: {d.id}</span>
                </div>
                <button type="button" onClick={() => removeDoctor(d.id)} className="text-[10px] text-rose-600 hover:underline" data-testid={`clinic-profile-remove-doctor-${i}`}>
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Fld label="Name" required>
                  <Input value={d.name} onChange={(e) => updDoctor(d.id, { name: e.target.value })} placeholder="Dr Name" data-testid={`clinic-profile-doctor-name-${i}`} />
                </Fld>
                <Fld label="Registration no.">
                  <Input value={d.regNo} onChange={(e) => updDoctor(d.id, { regNo: e.target.value })} data-testid={`clinic-profile-doctor-regno-${i}`} />
                </Fld>
                <Fld label="Qualifications" span2>
                  <Input value={d.qualifications} onChange={(e) => updDoctor(d.id, { qualifications: e.target.value })} placeholder="MBBS, MD, ..." data-testid={`clinic-profile-doctor-quals-${i}`} />
                </Fld>
              </div>
              <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Signature</div>
                {d.signature_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL || ""}${d.signature_url}`} alt="signature" className="h-10 max-w-[180px] object-contain bg-white border border-slate-200 rounded" />
                ) : (
                  <span className="text-[10px] text-slate-500 italic">No signature uploaded — printed name will be used.</span>
                )}
                <label className="inline-flex items-center gap-1 px-2 py-1 border border-dashed border-slate-300 rounded cursor-pointer hover:bg-white text-[10px]" data-testid={`clinic-profile-doctor-signature-upload-${i}`}>
                  <Upload size={10} />
                  {d.signature_url ? "Replace" : "Upload"} signature (PNG/SVG, ≤ 1 MB)
                  <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={(e) => uploadSignature(d.id, e.target.files?.[0])} className="hidden" />
                </label>
              </div>
            </div>
          ))}
        </section>

        {/* Preferred pharma companies — drives the "My Choice" brand tier
            on the Prescription table. Order = priority; first-available brand
            wins per molecule. Falls back to Economical tier when none of the
            enabled companies has a brand for a molecule. */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              <Pill size={14} className="text-pink-600" /> My Choice — preferred pharma companies
            </div>
            <Button size="sm" variant="outline" onClick={resetCompanies} className="gap-1.5 text-[11px]" data-testid="clinic-profile-reset-companies">
              <RefreshCw size={11} /> Reset order
            </Button>
          </div>
          <div className="text-[11px] text-slate-600 leading-snug">
            Drag-priority list used when you tap <em>My Choice</em> above the Prescription table.
            For each molecule the app picks the brand from the <strong>topmost enabled</strong> company that has one on file.
            If no enabled company has a brand for that molecule, the row falls back to the Economical tier automatically.
          </div>
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100 bg-white" data-testid="clinic-profile-preferred-companies">
            {prefList.map((co, i) => (
              <div key={co} className="flex items-center gap-2 px-3 py-2" data-testid={`clinic-profile-company-${i}`}>
                <span className="text-[10px] font-semibold text-slate-400 w-4 text-right tabular">{i + 1}.</span>
                <span className="text-sm text-slate-800 flex-1">{co}</span>
                <button
                  type="button"
                  onClick={() => moveCompany(co, "up")}
                  disabled={i === 0}
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`clinic-profile-company-up-${i}`}
                  title="Move up"
                ><ArrowUp size={12} /></button>
                <button
                  type="button"
                  onClick={() => moveCompany(co, "down")}
                  disabled={i === prefList.length - 1}
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`clinic-profile-company-down-${i}`}
                  title="Move down"
                ><ArrowDown size={12} /></button>
                <button
                  type="button"
                  onClick={() => disableCompany(co)}
                  className="text-[10px] text-rose-600 hover:underline pl-1"
                  data-testid={`clinic-profile-company-disable-${i}`}
                >Disable</button>
              </div>
            ))}
            {prefList.length === 0 && (
              <div className="px-3 py-2 text-[11px] text-slate-500 italic">
                All companies disabled — every row will fall back to the Economical tier.
              </div>
            )}
          </div>
          {disabledCompanies.length > 0 && (
            <div className="flex items-center flex-wrap gap-1.5 pt-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Disabled:</span>
              {disabledCompanies.map((co) => (
                <button
                  key={co}
                  type="button"
                  onClick={() => enableCompany(co)}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-slate-300 bg-slate-50 text-slate-600 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700"
                  data-testid={`clinic-profile-company-enable-${co}`}
                >+ {co}</button>
              ))}
            </div>
          )}
        </section>

        {/* Rx footer */}
        <section className="space-y-3">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <FileText size={14} className="text-pink-600" /> Prescription footer / tagline (optional)
          </div>
          <Input value={form.footer} onChange={(e) => upd("footer", e.target.value)} placeholder="e.g. Save prescription for future reference · Call the clinic before your next visit" data-testid="clinic-profile-footer" />
          <div className="text-[10px] text-slate-500">Prints as a small line under the doctor's signature block.</div>
        </section>

        <div className="pt-2 flex justify-end gap-2 sticky bottom-0 bg-white -mx-6 px-6 py-3 border-t border-slate-200">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
            data-testid="clinic-profile-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save clinic profile
          </Button>
        </div>
      </div>
    </Layout>
  );
}

function Fld({ label, required, span2, children }) {
  return (
    <div className={span2 ? "md:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label} {required && <span className="text-rose-600">*</span>}
      </label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

// WhatsApp Automation provision — settings are stored now, automation stays
// OFF (manual wa.me mode) until the clinic connects an AiSensy/Gupshup key.
// Backup & data safety — doctor-controlled full backup download plus the
// weekly auto-export status (server pushes a ZIP to object storage).
function BackupSection() {
  const [status, setStatus] = useState(null);
  const [downloading, setDownloading] = useState(false);
  useEffect(() => {
    api.get("/backup/status").then((r) => setStatus(r.data)).catch((e) => console.debug("[silent]", e));
  }, []);
  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/backup/export", { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jm_clinic_backup_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded — keep it somewhere safe (drive/pen-drive)");
    } catch {
      toast.error("Backup failed — try again");
    }
    setDownloading(false);
  };
  const c = status?.counts || {};
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 mt-6" data-testid="backup-section">
      <div className="flex items-center gap-2">
        <ShieldCheck size={16} className="text-emerald-600" />
        <h2 className="font-heading text-lg font-semibold text-slate-900">Backup & data safety</h2>
      </div>
      <p className="text-xs text-slate-500 mt-1 max-w-xl">
        One tap downloads your complete clinic data — patients, visits, prescriptions, lab values, report metadata,
        diet plans, drafts, settings and the audit trail — as a ZIP you keep. The app also auto-exports a copy to
        secure cloud storage every 7 days.
      </p>
      <div className="mt-3 flex items-center gap-4 flex-wrap">
        <Button onClick={download} disabled={downloading} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="download-backup-btn">
          {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          {downloading ? "Preparing backup…" : "Download full backup (ZIP)"}
        </Button>
        <div className="text-[11px] text-slate-500" data-testid="backup-status-line">
          {status ? (
            <>
              <span className="tabular">{c.patients ?? "—"} patients · {c.visits ?? "—"} visits · {c.prescriptions ?? "—"} prescriptions · {c.reports ?? "—"} reports</span>
              <span className="ml-2">
                {status.auto?.last_auto
                  ? `· Last auto-backup: ${new Date(status.auto.last_auto).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`
                  : "· Auto-backup will run on the next weekly cycle"}
              </span>
            </>
          ) : "Loading status…"}
        </div>
      </div>
    </section>
  );
}

// Google Drive archive — one-time connect; then every finalized prescription
// PDF and the weekly backup ZIP land in the clinic's own Drive automatically.
function GDriveSection() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/gdrive/status").then((r) => setSt(r.data)).catch((e) => console.debug("[silent]", e));
  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search);
    if (q.get("drive") === "connected") toast.success("Google Drive connected — prescriptions will now auto-archive");
    if (q.get("drive") === "error") {
      const m = q.get("msg") || "";
      const friendly = {
        exchange_failed_invalid_client: "Google rejected the app's Client Secret — the server's Drive credentials are outdated. Redeploy with the latest settings or update the secret.",
        exchange_failed_invalid_grant: "Google rejected the sign-in code (expired or already used). Please click Connect and try once more in one go.",
        exchange_failed_redirect_mismatch: "Redirect URL mismatch — add this site's /api/oauth/drive/callback URL in Google Cloud Console → Credentials.",
        exchange_failed_network: "Server could not reach Google — please try again in a minute.",
        state_expired: "The sign-in session expired — please click Connect again.",
        scope_denied: "Drive permission was not granted — tick the Drive access box on the Google consent screen.",
      }[m];
      toast.error(friendly || `Drive connection failed: ${m || "try again"}`, { duration: 10000 });
    }
  }, []);
  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/oauth/drive/connect");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Google sign-in");
      setBusy(false);
    }
  };
  const test = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gdrive/test");
      toast.success("Test PDF uploaded to your Drive ✓");
      if (r.data.link) window.open(r.data.link, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test upload failed");
    }
    setBusy(false);
    load();
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Drive? New prescriptions will stop archiving (existing Drive files stay).")) return;
    await api.post("/gdrive/disconnect").catch(() => {});
    toast.info("Google Drive disconnected");
    load();
  };
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 mt-6" data-testid="gdrive-section">
      <div className="flex items-center gap-2">
        <HardDriveUpload size={16} className="text-blue-600" />
        <h2 className="font-heading text-lg font-semibold text-slate-900">Google Drive archive</h2>
        {st?.connected && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Connected</span>}
      </div>
      <p className="text-xs text-slate-500 mt-1 max-w-xl">
        Every finalized prescription is saved as a PDF into a <b>Prescribify Prescriptions</b> folder in your own
        Google Drive (organised by month), and the weekly backup ZIP goes to <b>Prescribify Backups</b> — an extra
        copy of your data that you fully own. The app can only touch files it creates, never your other Drive content.
      </p>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        {!st?.connected ? (
          <Button onClick={connect} disabled={busy || !st?.configured} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="gdrive-connect-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <HardDriveUpload size={14} />} Connect Google Drive
          </Button>
        ) : (
          <>
            <Button onClick={test} disabled={busy} variant="outline" className="gap-2" data-testid="gdrive-test-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Send test file
            </Button>
            <Button onClick={disconnect} variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="gdrive-disconnect-btn">
              Disconnect
            </Button>
          </>
        )}
        <div className="text-[11px] text-slate-500" data-testid="gdrive-status-line">
          {st ? (
            st.connected
              ? <>Connected as <b>{st.account_email}</b> · {st.uploads} file(s) archived{st.last_upload ? ` · last: ${st.last_upload.name}` : ""}</>
              : st.configured ? "Not connected yet — one-tap Google sign-in (doctor account)" : "Server credentials missing"
          ) : "Loading…"}
        </div>
      </div>
    </section>
  );
}

function WaGatewaySection() {
  const [cfg, setCfg] = React.useState(null);
  const [apiKey, setApiKey] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.get("/wa/settings").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  if (!cfg) return null;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/wa/settings", { ...cfg, api_key: apiKey });
      setCfg(r.data);
      setApiKey("");
      toast.success("WhatsApp gateway settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-2" data-testid="wa-gateway-section">
      <div className="text-sm font-semibold text-slate-800">WhatsApp Automation (future-ready)</div>
      <div className="text-[11px] text-slate-500 leading-relaxed">
        Today every WhatsApp message is <strong>manual</strong> — you review and press send from your own number, full control.
        This panel is a provision: connect an AiSensy or Gupshup account here later and automation (auto-send Rx, refill nudges,
        read receipts) becomes a one-tap switch. Patient consent is already being recorded at registration.
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Provider</label>
          <select
            value={cfg.provider}
            onChange={(e) => setCfg({ ...cfg, provider: e.target.value, enabled: e.target.value === "manual" ? false : cfg.enabled })}
            className="h-9 mt-0.5 block border border-slate-300 rounded-md text-sm px-2 bg-white"
            data-testid="wa-provider-select"
          >
            <option value="manual">Manual (wa.me — current)</option>
            <option value="aisensy">AiSensy</option>
            <option value="gupshup">Gupshup</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Sender number</label>
          <Input value={cfg.sender_number} onChange={(e) => setCfg({ ...cfg, sender_number: e.target.value })} placeholder="91XXXXXXXXXX" className="h-9 mt-0.5 w-40" data-testid="wa-sender-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">API key {cfg.has_api_key && <span className="text-emerald-600">(saved ✓)</span>}</label>
          <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={cfg.has_api_key ? "•••••• (leave blank to keep)" : "Paste BSP API key"} className="h-9 mt-0.5 w-56" data-testid="wa-apikey-input" />
        </div>
        <label className={`flex items-center gap-1.5 text-[11px] mb-2 ${cfg.provider === "manual" ? "text-slate-300" : "text-slate-700"} cursor-pointer select-none`}>
          <input type="checkbox" disabled={cfg.provider === "manual"} checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="accent-emerald-600" data-testid="wa-enabled-toggle" />
          Enable automation
        </label>
        <Button onClick={save} disabled={saving} size="sm" className="bg-slate-900 hover:bg-slate-700 text-white mb-1" data-testid="wa-save-btn">
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </section>
  );
}
